<template>
  <div class="box-wrap">
    <div class="box">
      <p class="auth-brand"><BrandLogo large /></p>
      <template v-if="!done">
        <h1>{{ $t('Choose a new password') }}</h1>
        <form @submit.prevent="submit">
          <label for="new-password">{{ $t('New password') }}</label>
          <input id="new-password" v-model="password" type="password" minlength="8" required
                 autocomplete="new-password" :placeholder="$t('At least 8 characters')">
          <label for="repeat-password">{{ $t('Repeat it') }}</label>
          <input id="repeat-password" v-model="repeat" type="password" minlength="8" required
                 autocomplete="new-password">
          <button type="submit" :disabled="busy">{{ busy ? $t('Saving...') : $t('Save the password') }}</button>
        </form>
        <p v-if="error" class="error-message">{{ error }}</p>
        <router-link v-if="expired" to="/forgot" class="back">{{ $t('Ask for a new link') }}</router-link>
      </template>
      <template v-else>
        <h1>{{ $t('Password changed') }}</h1>
        <p class="done">{{ $t('You can log in with your new password now.') }}</p>
        <router-link to="/login" class="cta">{{ $t('Log in') }}</router-link>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import api from '../api'
import BrandLogo from '../components/BrandLogo.vue'
import { t } from '../i18n'

const route = useRoute()
const password = ref('')
const repeat = ref('')
const busy = ref(false)
const done = ref(false)
const error = ref('')
const expired = ref(false)

const submit = async () => {
  error.value = ''
  if (password.value !== repeat.value) {
    error.value = t('The two passwords differ.')
    return
  }
  busy.value = true
  try {
    await api.post('/api/auth/reset-password', {
      token: String(route.query.token || ''),
      password: password.value
    })
    done.value = true
  } catch (err) {
    const detail = err.response?.data?.detail
    error.value = t((Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : detail) || 'Could not reach the server.')
    expired.value = err.response?.status === 400
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.auth-brand {
  display: flex;
  justify-content: center;
  margin: 0 0 22px;
}

.box-wrap {
  display: flex;
  justify-content: center;
  padding: 60px 20px;
}

.box {
  width: 100%;
  max-width: 400px;
  padding: 28px;
  border-radius: 10px;
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.dark .box {
  background: #2a2a2a;
}

h1 {
  margin: 0 0 16px 0;
  font-size: 22px;
}

label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
}

input {
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 14px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font: inherit;
}

button,
.cta {
  display: block;
  box-sizing: border-box;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 6px;
  background: #4CAF50;
  color: white;
  font: inherit;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
}

button:disabled {
  opacity: 0.6;
  cursor: default;
}

.done {
  padding: 12px;
  border-radius: 6px;
  background: #e8f5e9;
  color: #2e7d32;
  font-size: 14px;
}

.error-message {
  padding: 10px;
  border-radius: 4px;
  background: #ffebee;
  color: #c62828;
  font-size: 14px;
}

.back {
  display: block;
  margin-top: 12px;
  font-size: 14px;
  text-align: center;
}
</style>
